import React, { useEffect, useRef, useState } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import "./HtmlBodyField.scss";

// ReactQuill is a WYSIWYG editor: it only understands its own toolbar
// formats (bold/italic/underline/strike, headers, lists, links). Anything
// pasted into it goes through Quill's clipboard matchers first, which
// convert the incoming markup into Quill's internal Delta model and
// silently discard everything that model can't represent - <!DOCTYPE>,
// <head>, <style>, <table>, custom classes, inline layout, all of it. A
// full custom HTML email template pasted straight into Quill comes out the
// other side as a handful of plain paragraphs/headers containing only the
// visible text. That's still valid HTML (so a preview using it doesn't
// error), which is what made the previous version confusing - it "looked
// like text" because the actual document was already gone by the time
// anything tried to render it.
//
// Fix: intercept the paste at the DOM level, in the capture phase, before
// Quill's own paste handler (attached in the bubble phase on the same
// node) ever sees it. If the pasted content is a full document, redirect
// it straight into a raw HTML source textarea instead - no lossy
// conversion, no need for the user to remember to switch modes first.
const FULL_HTML_DOC_REGEX = /<!DOCTYPE\s+html|<html[\s>]/i;

const QUILL_MODULES = {
  toolbar: [
    ["bold", "italic", "underline", "strike"],
    [{ header: [1, 2, 3, false] }],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "clean"],
  ],
};

/**
 * Drop-in replacement for a plain ReactQuill body field. Same props as a
 * controlled text input: `value` / `onChange(nextHtml)`.
 */
const HtmlBodyField = ({
  value,
  onChange,
  className,
  onEditorReady,
  onSourceReady,
  onFocus,
}) => {
  const [mode, setMode] = useState(
    FULL_HTML_DOC_REGEX.test(value || "") ? "source" : "rich",
  );
  const quillWrapperRef = useRef(null);

  // Only needs to be active while Quill is actually mounted (rich mode).
  useEffect(() => {
    const wrapper = quillWrapperRef.current;
    if (!wrapper || mode !== "rich") return undefined;

    const handlePasteCapture = (event) => {
      const pasted =
        event.clipboardData?.getData("text/html") ||
        event.clipboardData?.getData("text/plain") ||
        "";
      if (FULL_HTML_DOC_REGEX.test(pasted)) {
        // Stop this paste before Quill's bubble-phase handler runs.
        event.preventDefault();
        event.stopPropagation();
        setMode("source");
        onChange(pasted);
      }
    };

    wrapper.addEventListener("paste", handlePasteCapture, true);
    return () => wrapper.removeEventListener("paste", handlePasteCapture, true);
  }, [mode, onChange]);

  return (
    <div className={`html-body-field ${className || ""}`}>
      <div className="html-body-field__toolbar">
        <button
          type="button"
          className={`html-body-field__mode-btn ${
            mode === "rich" ? "html-body-field__mode-btn--active" : ""
          }`}
          onClick={() => setMode("rich")}
        >
          Rich text
        </button>
        <button
          type="button"
          className={`html-body-field__mode-btn ${
            mode === "source" ? "html-body-field__mode-btn--active" : ""
          }`}
          onClick={() => setMode("source")}
        >
          HTML source
        </button>
      </div>

      {mode === "rich" ? (
        <div ref={quillWrapperRef} onFocus={onFocus}>
          <ReactQuill
            className="automation-modal__html-editor"
            theme="snow"
            value={value || ""}
            onChange={onChange}
            ref={onEditorReady}
            modules={QUILL_MODULES}
          />
        </div>
      ) : (
        <textarea
          className="automation-modal__html-source"
          ref={onSourceReady}
          onFocus={onFocus}
          rows={16}
          spellCheck={false}
          placeholder="<html>...paste a full custom email template here...</html>"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
        />
      )}

      <small className="form-note">
        {mode === "rich"
          ? "Formats as HTML automatically. Pasting a full custom template (its own <style>, tables, etc.) switches to HTML source on its own, so nothing gets stripped."
          : "Raw HTML, used exactly as typed."}
      </small>
    </div>
  );
};

export default HtmlBodyField;
